<template lang="pug">
div
    v-select.selector(
      ref="fieldSelector"
      v-model="selectedField"
      :items="computedItems"
      @change="handleSelectChange"
      item-text="name"
      item-value="value"
      item-disabled="disabled"
      :placeholder="placeHolderText"
    )
        template(slot="prepend-item")
          v-list-tile.pr-2
            v-list-tile-content
              v-text-field(
                v-model="searchField"
                append-icon="search"
                placeholder="Find a field..."
              )
          v-divider.mt-2
          v-list-tile(
            ripple
            @click="deselectField"
          )
            v-list-tile-avatar.avatar-option
              v-icon cancel
            v-list-tile-content
              v-list-tile-title Don't import
          v-divider.mt-2
          v-list-tile(
            ripple
            @click="openCustomField"
          )
            v-list-tile-avatar.avatar-option
              v-icon add_circle
            v-list-tile-content Add a custom field...
          v-divider.mt-2
        template(slot="selection" slot-scope="{ item }")
          span.primary--text {{ item.name }}
        template(slot="item" slot-scope="{ item }")
          span {{ item.name }}
    liondesk-dialog(
      width="600px"
      v-model="showCustomFieldDialog"
      dialog-title="ADD CUSTOM FIELD"
      @click:cancel="closeCustomFieldDialog"
      @click:ok="saveCustomField"
    )
      template(slot="body")
        v-container.px-0
          v-layout(row wrap)
            v-flex(xs12)
              v-select(
               label="Select type of data"
               :items="customFieldTypes"
               v-model="newCustomField.dataType"
              )
            v-flex(xs12)
              v-text-field(label="Custom field name" v-model="newCustomField.name")
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import LiondeskDialog from '@/Core/components/LiondeskDialog';

import CONTACT_FIELDS from '../config/contact-fields';
import ADDRESS_FIELDS from '../config/address-fields';

export default {
  name: 'FieldSelector',
  components: {
    LiondeskDialog,
  },
  async created() {
    if (!this.customFieldsWithTypes) {
      await this.findCustomFields();
    }

    if (!this.customFieldTypes) {
      await this.findCustomFieldTypes();
    }
  },
  props: {
    hideFields: {
      type: Array,
      default: () => [],
    },
    value: String,
  },
  data() {
    return {
      fields: CONTACT_FIELDS,
      addressFields: ADDRESS_FIELDS,
      selectedField: '',
      searchField: '',
      showCustomFieldDialog: false,
      newCustomField: {
        name: '',
        dataType: '',
      },
      isExcludedField: false,
    };
  },
  computed: {
    ...mapState('customFields', {
      customFieldTypes: 'customFieldTypes',
      createdCustomField: 'customField',
    }),
    ...mapGetters('contactsTable', ['customFieldsWithTypes']),
    computedBaseFields() {
      const fields = this.fields.map(item => ({
        name: item.name,
        value: item.value,
        type: item.type,
        disabled: this.isSelectedField(item.value),
      }));
      return fields.filter(field => this.filterField(field));
    },
    computedAddressFields() {
      const selectAddressFields = this.addressFields.map(addressType => {
        const fields = addressType
          .map(field => {
            if (field.header || field.divider) {
              return field;
            }

            return {
              name: field.name,
              value: field.value.addressTypeValue,
              disabled: this.isSelectedField(field.value.addressTypeValue),
            };
          })
          .filter(field => this.filterField(field));
        if (!fields.every(field => field.header || field.divider)) {
          return fields;
        }
        return [];
      });
      return [].concat(...selectAddressFields);
    },
    computedCustomFields() {
      if (!this.customFieldsWithTypes.length) {
        return [];
      }

      const fields = this.customFieldsWithTypes
        .map(item => ({
          name: item.name,
          value: `custom_field_${item.id}`,
          type: item.type,
          disabled: this.isSelectedField(`custom_field_${item.id}`),
        }))
        .filter(field => this.filterField(field));

      if (fields.length) {
        return [{ header: 'Custom fields' }, { divider: true }, ...fields];
      }
      return [];
    },
    computedItems() {
      let fields = this.computedBaseFields;
      fields = fields.concat(this.computedAddressFields);
      fields = fields.concat(this.computedCustomFields);
      return fields;
    },
    placeHolderText() {
      return this.isExcludedField ? `Don't Import` : 'Match to...';
    },
  },
  methods: {
    ...mapMutations('customFields', { clearCreatedCustomField: 'clearMessages'}),
    ...mapActions('customFields', ['findCustomFieldTypes', 'createCustomField']),
    ...mapActions('contactsTable', ['findCustomFields']),
    deselectField() {
      this.selectedField = '';
      this.isExcludedField = true;
      this.$refs.fieldSelector.blur();
      this.$emit('input', this.selectedField);
    },
    isSectionElement(field) {
      return field.header || field.divider;
    },
    filterField(field) {
      const isSectionElement = this.isSectionElement(field);
      const isSearchOption =
        field.name && field.name.toLowerCase().includes(this.searchField.toLowerCase());
      return isSectionElement || isSearchOption;
    },
    openCustomField() {
      this.showCustomFieldDialog = true;
    },
    isSelectedField(fieldValue) {
      if (fieldValue === this.selectedField) {
        return true;
      }
      return this.hideFields.some(value => value === fieldValue);
    },
    closeCustomFieldDialog() {
      this.newCustomField = { name: '', dataType: '' };
      this.showCustomFieldDialog = false;
    },
    async saveCustomField() {
      const { name, dataType } = this.newCustomField;
      const payload = { name, data_type: dataType };

      await this.createCustomField(payload);
      await this.findCustomFields();

      this.selectedField = `custom_field_${this.createdCustomField.id}`;
      this.$emit('input', this.selectedField);
      this.$refs.fieldSelector.blur();
      
      this.clearCreatedCustomField();
      this.closeCustomFieldDialog();
    },
    handleSelectChange(value) {
      this.$emit('input', value);
    },
  },
  watch: {
    value(newValue) {
      this.selectedField = newValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.selector {
  min-width: 150px;
  margin-top: 0;
  margin-bottom: -15px;
  padding-top: 0;
  /deep/ .v-input__slot::before {
    border: none;
  }
}

.avatar-option {
  min-width: 0px;
}
</style>
